import { LoadFailed, QueryLoading } from '../components/QueryState';
import { HealthyHomesCompletion } from '../features/reports/HealthyHomesCompletion';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatAddress, formatRef } from '@promanage/core';
import { Button, Panel, PanelHeader } from '@promanage/ui';
import { PropertyReportPicker } from '../features/reports/PropertyReportPicker';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { usePortfolioScope } from '../features/portfolio/scope';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { HealthyHomesReport } from '../features/reports/HealthyHomesReport';
import { parseHealthyHomesSearch } from '../features/reports/healthyHomesSearch';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/reports/healthy-homes')({
  validateSearch: parseHealthyHomesSearch,
  component: HealthyHomesPage,
});

function HealthyHomesPage() {
  const selection = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { scope } = usePortfolioScope();
  const properties = useQuery(
    trpc.property.list.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load properties' }, staleTime: 30_000 },
    ),
  );
  const report = useQuery({
    ...trpc.reports.healthyHomes.queryOptions(
      { refSeq: selection.property ?? 1 },
      { meta: { errorTitle: 'Could not prepare this statement' }, staleTime: 0 },
    ),
    enabled: selection.property !== undefined,
  });
  const statement = selection.property === undefined ? undefined : report.data;
  const ready = statement !== undefined && !report.isFetching && !report.isError;
  return (
    <ReportWorkspace
      title="Healthy Homes statement"
      subtitle="Prepare a statement from the property's saved assessment"
      actions={
        <>
          <ExportPdfButton
            label="Download PDF"
            disabled={!ready}
            onExport={async () => {
              if (!ready) return;
              const { exportHealthyHomesPdf } =
                await import('@promanage/documents/healthy-homes-pdf');
              exportHealthyHomesPdf(statement);
            }}
          />
          <Button size="sm" disabled={!ready} onClick={() => window.print()}>
            Print
          </Button>
        </>
      }
      controls={
        <Panel>
          <PanelHeader
            title="Report selection"
            subtitle="Choose a property to prepare its statement."
          />
          <div className="max-w-2xl px-5 pb-5 pt-4">
            <PropertyReportPicker
              options={(properties.data ?? []).map((property) => ({
                refSeq: property.refSeq,
                label: `${formatAddress(property)} - ${formatRef('property', property.refSeq)}`,
              }))}
              value={selection.property}
              selectedLabel={
                selection.property === undefined
                  ? undefined
                  : (statement?.address ?? formatRef('property', selection.property))
              }
              pending={properties.isPending}
              onChange={(property) => {
                void navigate({ search: { property }, replace: true });
              }}
            />
            {properties.isError ? (
              <LoadFailed
                retry={() => void properties.refetch()}
                retrying={properties.isFetching}
              />
            ) : null}
          </div>
        </Panel>
      }
    >
      <div className="flex flex-col gap-4">
        {selection.property === undefined ? (
          <Panel className="p-5 text-[0.8125rem] text-ink-muted">
            Choose a property to preview its Healthy Homes statement.
          </Panel>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 print:hidden">
              <Link
                to="/properties/$ref"
                params={{ ref: formatRef('property', selection.property) }}
                search={{ tab: 'healthy-homes' }}
                className="text-[0.8125rem] font-semibold text-accent hover:underline"
              >
                Open Healthy Homes register
              </Link>
              <Button
                size="sm"
                variant="secondary"
                disabled={report.isFetching}
                onClick={() => void report.refetch()}
              >
                Refresh assessment
              </Button>
            </div>
            {report.isError ? (
              <LoadFailed retry={() => void report.refetch()} retrying={report.isFetching} />
            ) : report.isPending ? (
              <QueryLoading label="Preparing statement..." />
            ) : statement ? (
              <>
                <HealthyHomesCompletion statement={statement} />
                <HealthyHomesReport statement={statement} />
              </>
            ) : null}
          </>
        )}
      </div>
    </ReportWorkspace>
  );
}
